import { Table } from 'antd';
import { useSelector } from 'react-redux';
import useIsUiHidden from '../../hooks/dom/useIsUiHidden';
import { useState, useEffect, useMemo } from 'react';

const ProfTable = ({ ...props }) => {
  const isUiHidden = useIsUiHidden();
  const { informationExpanded } = useSelector(state => state.professionalReport);

  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [internalExpandedRowKeys, setInternalExpandedRowKeys] = useState([]);

  // Check if parent is controlling expanded state
  const isExpandedControlledByParent = props.expandable?.expandedRowKeys !== undefined;

  // Get all expandable row keys from the data (only used when not controlled by parent)
  const allExpandableKeys = useMemo(() => {
    if (isExpandedControlledByParent || !props.dataSource || !props.expandable) return [];

    return props.dataSource
      .filter((record, index) => {
        // Check if row is expandable using the expandable.rowExpandable function if provided
        if (props.expandable?.rowExpandable) {
          return props.expandable.rowExpandable(record);
        }
        // If no rowExpandable function, assume all rows are expandable
        return true;
      })
      .map(record => {
        // Use the rowKey if provided, otherwise use the record key or index
        if (props.rowKey) {
          return typeof props.rowKey === 'function' ? props.rowKey(record) : record[props.rowKey];
        }
        return record.key || record.id;
      })
      .filter(key => key !== undefined);
  }, [props.dataSource, props.expandable, props.rowKey, isExpandedControlledByParent]);

  useEffect(() => {
    if (isExpandedControlledByParent) return;

    if (informationExpanded || isUiHidden) {
      setInternalExpandedRowKeys(allExpandableKeys);
    } else {
      setInternalExpandedRowKeys([]);
    }
  }, [informationExpanded, allExpandableKeys, isExpandedControlledByParent]);

  const handlePaginationChange = (newPage, newPageSize) => {
    setCurrent(newPage);
    setPageSize(newPageSize);
  };

  const handleInternalExpand = (expanded, record) => {
    const key = props.rowKey
      ? typeof props.rowKey === 'function'
        ? props.rowKey(record)
        : record[props.rowKey]
      : record.key || record.id;

    if (expanded) {
      setInternalExpandedRowKeys(prev => [...prev, key]);
    } else {
      setInternalExpandedRowKeys(prev => prev.filter(k => k !== key));
    }

    if (props.expandable?.onExpand) {
      props.expandable.onExpand(expanded, record);
    }
  };

  // Enhanced rowClassName function that handles expandable tables
  const getEnhancedRowClassName = (record, index) => {
    let className = '';

    // Apply alternating row styling for expandable tables
    const isOdd = index % 2 === 1;
    className += isOdd ? 'table-row-odd' : 'table-row-even';

    // If the original props had a rowClassName, merge it
    if (props.rowClassName) {
      const originalClassName =
        typeof props.rowClassName === 'function' ? props.rowClassName(record, index) : props.rowClassName;
      className += ` ${originalClassName}`;
    }

    return className.trim();
  };

  const pagination =
    !informationExpanded || isUiHidden
      ? props?.pagination
      : props?.pagination
      ? {
          current,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: handlePaginationChange,
        }
      : false;

  const expandableConfig = props.expandable
    ? {
        ...props.expandable,
        ...(isExpandedControlledByParent
          ? {}
          : {
              expandedRowKeys: internalExpandedRowKeys,
              onExpand: handleInternalExpand,
            }),
      }
    : undefined;

  return (
    <Table {...props} pagination={pagination} rowClassName={getEnhancedRowClassName} expandable={expandableConfig} />
  );
};

export default ProfTable;
